import React from 'react'
import { Button } from '@fluentui/react-components'
import { CodeRegular, CloudRegular, PrintRegular, ChatRegular, CallRegular } from '@fluentui/react-icons'
import Tilt from 'react-vanilla-tilt'
import DataFlowAnimation from '../DataFlowAnimation'
import TerminalIntro from '../TerminalIntro'
import { useEffects } from '../../contexts/EffectsContext'

interface AppHeaderProps {
	className?: string
}

export const AppHeader: React.FC<AppHeaderProps> = ({ className }) => {
	const { effects } = useEffects()

	const header = (
		<header className={className || 'App-header'}>
			<div className={`App-logo-container${effects.logoMotion ? ' fx-logo-motion' : ''}`}>
				<CodeRegular className="App-logo App-logo-icon" />
				<DataFlowAnimation />
				<CloudRegular className="App-logo App-logo-reverse App-logo-icon" />
			</div>

			<div className="App-identity">
				<h1 className={`App-name${effects.logoMotion ? ' fx-gradient-shift' : ''}`}>
					Martin <span className="App-name-last">Bondy&nbsp;Pětvalský</span>
				</h1>
				<p className="App-title">Fullstack Microsoft 365 Developer</p>
			</div>

			<p className="App-intro-text App-intro-headline">
				Enterprise M365 solutions.
			</p>

			<TerminalIntro />

			<p className="App-intro-text App-intro-sub">
				Specializing in SharePoint Framework (SPFx), Microsoft Teams apps, and Azure
				integrations — custom TypeScript &amp; .NET development with CI/CD, not
				no-code tools like Power Automate.
			</p>

			<p className="App-intro-text App-intro-sub App-intro-note">
				Most enterprise work is covered by NDAs so I added interactive demos to give you basic examples.
			</p>

			<div className="App-cta-group">
				<Button
					appearance="primary"
					size="large"
					icon={<ChatRegular style={{ fontSize: '24px' }} />}
					className={`App-link-button${effects.ctaPulse ? ' fx-pulse' : ''}`}
					onClick={() => document.getElementById('section-teamsIntegration')
						?.scrollIntoView({ behavior: 'smooth' })}
					aria-label="Talk or chat with Martin's CV"
				>
					or <CallRegular style={{ fontSize: '24px', margin: '0 2px', verticalAlign: 'middle' }} /> with my CV
				</Button>
				<a href="#content-start" aria-label="View my work">
					<Button
						appearance="primary"
						size="large"
						className={`App-link-button${effects.ctaPulse ? ' fx-pulse' : ''}`}
					>
						View My Work
					</Button>
				</a>
				<a
					href="https://www.linkedin.com/in/martinpetvalsky/"
					target="_blank"
					rel="noopener noreferrer"
					aria-label="Connect with me on LinkedIn"
				>
					<Button
						appearance="primary"
						size="large"
						style={{ backgroundColor: '#0A66C2', borderColor: '#0A66C2' }}
						icon={
							<svg
								width="20"
								height="20"
								viewBox="0 0 24 24"
								fill="currentColor"
								xmlns="http://www.w3.org/2000/svg"
								aria-hidden="true"
							>
								<path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.761 0 5-2.239 5-5v-14c0-2.761-2.239-5-5-5zm-11 19h-3v-10h3v10zm-1.5-11.268c-.966 0-1.75-.784-1.75-1.75s.784-1.75 1.75-1.75 1.75.784 1.75 1.75-.784 1.75-1.75 1.75zm13.5 11.268h-3v-5.604c0-1.337-.026-3.063-1.868-3.063-1.868 0-2.154 1.459-2.154 2.967v5.7h-3v-10h2.881v1.367h.041c.401-.761 1.379-1.563 2.838-1.563 3.036 0 3.6 2 3.6 4.594v5.602z" />
							</svg>
						}
						className={`App-link-button${effects.ctaPulse ? ' fx-pulse' : ''}`}
					>
						LinkedIn
					</Button>
				</a>
				<Button
					appearance="primary"
					size="large"
					icon={<PrintRegular />}
					onClick={() => window.print()}
					aria-label="Print CV as PDF"
					className={`App-link-button${effects.ctaPulse ? ' fx-pulse' : ''}`}
				>
					Print CV
				</Button>
			</div>
		</header>
	)

	if (!effects.tilt) {
		return header
	}

	return (
		<Tilt
			style={{}}
			options={{
				scale: 1.05,
				max: 25,
				speed: 400,
				glare: true,
				'max-glare': 0.5,
			}}
		>
			{header}
		</Tilt>
	)
}
